<template>
  <header class="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 shadow-md">
    <div class="container mx-auto px-4">
      <div class="flex items-center justify-between h-16">
        <!-- Logo -->
        <router-link to="/" class="flex items-center">
          <img src="@/assets/logo.svg" alt="Aon Netherlands" class="h-10 w-auto" />
        </router-link>
        
        <!-- Desktop Menu -->
        <nav class="hidden md:flex items-center space-x-6">
          <router-link 
            v-for="item in navItems" 
            :key="item.path" 
            :to="item.path"
            class="text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary font-medium transition-colors rounded-md px-2 py-1 border border-transparent"
            active-class=""
            exact-active-class="text-primary dark:text-white bg-primary/20 dark:bg-primary/45 border-primary ring-1 ring-primary/30 font-semibold"
          >
            {{ $t(item.label) }}
          </router-link>
        </nav>
        
        <!-- Right Menu (Language Selector & Theme Toggle) -->
        <div class="flex items-center space-x-4">
          <!-- Language Selector -->
          <div class="relative">
            <button 
              @click="toggleLanguageMenu" 
              class="flex items-center text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary"
            >
              <span class="mr-1">{{ currentLanguage.code.toUpperCase() }}</span>
              <i class="fas fa-chevron-down text-xs"></i>
            </button>
            
            <!-- Language Dropdown -->
            <div 
              v-show="isLanguageMenuOpen" 
              class="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-700 rounded-md shadow-lg overflow-hidden z-10"
            >
              <div class="py-1">
                <button 
                  v-for="lang in languages" 
                  :key="lang.code"
                  @click="changeLanguage(lang.code)"
                  class="flex items-center px-4 py-2 text-sm w-full text-left hover:bg-gray-100 dark:hover:bg-gray-700"
                  :class="{ 'text-primary font-medium': $i18n.locale === lang.code }"
                >
                  <span class="mr-2">{{ lang.flag }}</span>
                  <span>{{ lang.name }}</span>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Theme Toggle -->
          <button 
            @click="toggleTheme" 
            class="text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary"
          >
            <i :class="isDarkMode ? 'fas fa-sun' : 'fas fa-moon'"></i>
          </button>
          
          <!-- Mobile Menu Button -->
          <button 
            @click="toggleMobileMenu" 
            class="md:hidden text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary"
          >
            <i :class="isMobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'"></i>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Mobile Menu -->
    <div 
      v-show="isMobileMenuOpen" 
      class="md:hidden bg-white dark:bg-gray-900 shadow-lg"
    >
      <div class="container mx-auto px-4 py-3">
        <nav class="flex flex-col space-y-3">
          <router-link 
            v-for="item in navItems" 
            :key="item.path" 
            :to="item.path"
            class="text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary font-medium transition-colors py-2 rounded-md px-3 border border-transparent"
            active-class=""
            exact-active-class="text-primary dark:text-white bg-primary/20 dark:bg-primary/45 border-primary ring-1 ring-primary/30 font-semibold"
            @click="isMobileMenuOpen = false"
          >
            {{ $t(item.label) }}
          </router-link>
        </nav>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { locale } = useI18n();

// Navigation Items
const navItems = [
  { path: '/', label: 'navigation.home' },
  { path: '/approach', label: 'navigation.approach' },
  { path: '/evidence', label: 'navigation.evidence' },
  { path: '/insights', label: 'navigation.insights' },
  { path: '/next-step', label: 'navigation.nextStep' },
];

// Language Menu
const isLanguageMenuOpen = ref(false);
const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
];

const currentLanguage = computed(() => {
  return languages.find(lang => lang.code === locale.value) || languages[0];
});

const toggleLanguageMenu = () => {
  isLanguageMenuOpen.value = !isLanguageMenuOpen.value;
};

const changeLanguage = (langCode: string) => {
  locale.value = langCode;
  localStorage.setItem('preferredLanguage', langCode);
  document.documentElement.dir = langCode === 'ar' ? 'rtl' : 'ltr';
  isLanguageMenuOpen.value = false;
};

// Theme Toggle
const isDarkMode = ref(false);

const toggleTheme = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark');
    localStorage.setItem('theme', 'dark');
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', 'light');
  }
};

// Check for saved theme preference
if (typeof window !== 'undefined') {
  const savedTheme = localStorage.getItem('theme');
  isDarkMode.value = savedTheme === 'dark' || 
    (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark');
  }
}

// Mobile Menu
const isMobileMenuOpen = ref(false);

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};
</script>
